import api from "@/utils/axios";

const MANUFACTURER_API_URL = `/manufacturers`;

export default {
  namespaced: true,
  state: {
    manufacturer: null,
    manufacturers: [],
    meta: {},
    loading: false,
    error: null,
  },
  mutations: {
    SET_MANUFACTURER(state, manufacturer) {
      state.manufacturer = manufacturer;
    },
    SET_MANUFACTURERS(state, manufacturers) {
      state.manufacturers = manufacturers;
    },
    SET_META(state, meta) {
      state.meta = meta;
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_MANUFACTURER(state) {
      state.manufacturer = null;
      state.error = null;
    },
    CLEAR_MANUFACTURERS(state) {
      state.manufacturers = [];
      state.meta = {};
      state.error = null;
    },
  },
  actions: {
    async fetchManufacturer({ commit }, manufacturerId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${MANUFACTURER_API_URL}/${manufacturerId}/`);
        commit("SET_MANUFACTURER", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch manufacturer"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async selectManufacturers({ commit }, searchTerm = "") {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${MANUFACTURER_API_URL}/select/`, {
          params: { q: searchTerm },
        });
        return res.data.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch categories"
        );
        return [];
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async filterManufacturers({ commit }, payload = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${MANUFACTURER_API_URL}/filter/`, payload);
        commit("SET_MANUFACTURERS", res.data.data);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch manufacturers"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async lazyLoadManufacturers({ commit, state }, payload = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${MANUFACTURER_API_URL}/filter/`, payload);
        const newManufacturers = res.data.data;
        const updatedList = [...state.manufacturers, ...newManufacturers];
        commit("SET_MANUFACTURERS", updatedList);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to load more manufacturers"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async addNewManufacturer({ commit }, data) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${MANUFACTURER_API_URL}/new/`, data);
        commit("SET_MANUFACTURER", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to create manufacturer"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    updateManufacturer: async function ({ commit }, { manufacturerId, data }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.patch(
          `${MANUFACTURER_API_URL}/${manufacturerId}/`,
          data
        );
        commit("SET_MANUFACTURER", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to update manufacturer"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async deleteManufacturer({ commit }, manufacturerId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        await api.delete(`${MANUFACTURER_API_URL}/${manufacturerId}/`);
        commit("CLEAR_MANUFACTURER");
        return true;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to delete manufacturer"
        );
        return false;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    clearManufacturer({ commit }) {
      commit("CLEAR_MANUFACTURER");
    },

    clearManufacturers({ commit }) {
      commit("CLEAR_MANUFACTURERS");
    },
  },
  getters: {
    manufacturer: (state) => state.manufacturer,
    manufacturers: (state) => state.manufacturers,
    manufacturerMeta: (state) => state.meta,
    manufacturerLoading: (state) => state.loading,
    manufacturerError: (state) => state.error,
  },
};
